import React, { useState, useEffect, useRef} from 'react';
import './view-notifications-styles.css';
import 'bulma/css/bulma.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExtranetLogo from '../../components/nav/ExtranetLogo';
import ApplicationName from '../../components/nav/ApplicationName';
import SearchBar from '../../components/nav/SearchBar';
import NotificationSettingComponent from './components/NotificationSettingComponent';
import NotificationCard from './components/NotificationCard';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import StaffNotification from './dataObjects/StaffNotification';
import AlertDialog from '../../components/elements/AlertDialog';


const GroupTypes = {
    Active: "Active",
    Snoozed: "Snoozed",
    Archived: "Archived"
}

const NotificationTypes = {
    NewTimesheetRequest:10
}

const GroupWidth = {
    narrow: 3,
    wide: 5
}

function NotificationsApp() {

    //set up browser title
    const pageTitle = 'Extranet - My Notifications';
    document.title = pageTitle;

    let Active: StaffNotification[] = new Array();
    let Snoozed: StaffNotification[] = new Array();
    let Archived: StaffNotification[] = new Array();


    //initial state set up
    const [staffNotifications, setNotifications] = useState(
        { Active, Snoozed, Archived}
    );
    
    const [addNewModalStatus, setAddNewModalStatus] = useState('');
    const [newNotificationFormData, setNewNotificationFormData] = useState({
        newNotificationTitle: "",
        newNotificationBody: "",
        newNotificationPriorityType: 0,
        newNotificationSnoozeType: "OneHour"
    });

    const [alertInfo, setAlertInfo] = useState({
        alertModalStatus: "",
        alertType: "",
        alertTitle: "",
        alertMessage: "",
    })

    const [archiveGroupStatus, setArchiveGroupStatus] = useState("is-hidden");
    const [groupWidth, setGroupWidth] = useState(GroupWidth.wide);

    useEffect(() => {
        updateCache();
        fetchPayload();
    }, []);

    const updateCache = async () => {
        fetch(`/Notifications/RefreshMyNotifications`)
        .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
            })
    }

    const fetchPayload = async () => {
        fetch(`/Notifications/GetMyNotifications`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    Active = res.result.activeNotifications;
                    Snoozed = res.result.snoozedNotifications;
                    Archived = res.result.archivedNotifications;
                    setNotifications({ Active, Snoozed, Archived });
                }
            });
    };

    //Search
    function getSearchResults(searchPhrase: string) {
        let searchInput = searchPhrase;
        if (searchInput.trim() == "") {
            fetchPayload();
            setGroupWidth(GroupWidth.wide);
            setArchiveGroupStatus('is-hidden');
        }
        else {
            fetch(`/Notifications/GetMyNotifications?searchPhrase=${searchInput}`)
                .then(res => res.json())
                .then(res => {
                    if (res.isError) {
                        displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                    }
                    else {
                        Active = res.result.activeNotifications;
                        Snoozed = res.result.snoozedNotifications;
                        Archived = res.result.archivedNotifications;
                        setNotifications({ Active, Snoozed, Archived });
                        //show archived folder
                        setGroupWidth(GroupWidth.narrow);
                        setArchiveGroupStatus('');
                    }
                });
        }
    }

    //Drag and Drop
    const reorder = (list: StaffNotification[], startIndex: number, endIndex: number) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        };
    const move = (source: StaffNotification[], destination: StaffNotification[], droppableSource: any, droppableDestination: any) => {
            const sourceClone = Array.from(source);
            const destClone = Array.from(destination);
            const [removed] = sourceClone.splice(droppableSource.index, 1);

            destClone.splice(droppableDestination.index, 0, removed);

            const result = { ...staffNotifications };
            result[droppableSource.droppableId as keyof typeof GroupTypes] = sourceClone;
            result[droppableDestination.droppableId as keyof typeof GroupTypes] = destClone;

            return result;
        };

    function onDragEnd(result: any) {
        const { source, destination } = result;

        // dropped outside the list
        if (!destination) {
            return;
        }
        const sGroupId = source.droppableId;
        const dGroupId = destination.droppableId;
        let sourceItem = staffNotifications[sGroupId as keyof typeof GroupTypes][source.index];
        let targetItem = staffNotifications[dGroupId as keyof typeof GroupTypes][destination.index];
        let sourceNotificationId = sourceItem.id;
        
        let targetNotificationId = 0;
        if (targetItem) {
            targetNotificationId = targetItem.id;
        }

        let targetGroupId = 1;
        switch (dGroupId) {
            case "Active":
                targetGroupId = 1;
                break;
            case "Snoozed":
                targetGroupId = 2;
                break;
            case "Archived":
                targetGroupId = 3;
                break;
        }

        var moveOptions = [
            `id=${sourceNotificationId}`,
            `targetGroupId=${targetGroupId}`,
            `targetNotificationId=${targetNotificationId}`
        ].join('&');

        fetch(`/Notifications/MoveNotification?${moveOptions}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    //fetchPayload(); save a DB call??
                    if (sGroupId === dGroupId) {
                        let list = staffNotifications[sGroupId as keyof typeof GroupTypes];
                        const items = reorder(list, source.index, destination.index);
                        const newState = { ...staffNotifications };
                        newState[sGroupId as keyof typeof GroupTypes] = items;
                        setNotifications(newState);
                    }
                    else {
                        let sList = staffNotifications[sGroupId as keyof typeof GroupTypes];
                        let dList = staffNotifications[dGroupId as keyof typeof GroupTypes];
                        const result = move(sList, dList, source, destination);
                        const newState = { ...staffNotifications };
                        newState[sGroupId as keyof typeof GroupTypes] = result[sGroupId as keyof typeof GroupTypes];
                        newState[dGroupId as keyof typeof GroupTypes] = result[dGroupId as keyof typeof GroupTypes];
                        setNotifications(newState);
                    }
                }
            });
    }

    //remove notification
    const [selectedItem, setSelectedItem] = useState({
        id: 0,
        index: 0,
        groupId: ""
    })

    function confirmRemoveItem(index: number, groupId: string, item: any) {
        displayAlertModal("is-warning", "Delete Notification...", "Do you want to delete this notification?");
        const currentItem = { ...selectedItem };
        currentItem.groupId = groupId;
        currentItem.id = item.id;
        currentItem.index = index;
        setSelectedItem(currentItem);
    }

    function removeItem() {
        let notificationId = selectedItem.id;
        let notificationIndex = selectedItem.index;
        let notificationGroupId = selectedItem.groupId;
        let list = staffNotifications[notificationGroupId as keyof typeof GroupTypes];
        fetch(`/Notifications/DeleteNotification?id=${notificationId}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    list.splice(notificationIndex, 1);
                    const newState = { ...staffNotifications };
                    newState[notificationGroupId as keyof typeof GroupTypes] = list;
                    setNotifications(newState);
                    closeAlertModal();
                }
            });
    }

    //click a link in the notification body, or click a button to archive item
   
    function archiveItem(item: any) {
        
        let sourceNotificationId = item.id;
        let targetGroupId = 3;
        let targetNotificationId = 0;
        if (staffNotifications.Archived.length > 0) {
            targetNotificationId = staffNotifications.Archived[0].id;
        }
       
        var moveOptions = [
            `id=${sourceNotificationId}`,
            `targetGroupId=${targetGroupId}`,
            `targetNotificationId=${targetNotificationId}`
        ].join('&');

        fetch(`/Notifications/MoveNotification?${moveOptions}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    fetchPayload();
                }
            });
    }

    function getMoreArchivedItems(event: any) {

        event.target.blur();

        let startIndex = staffNotifications.Archived.length;
        let pageSize = 20;

        var options = [
            `startIndex=${startIndex}`,
            `pageSize=${pageSize}`,
        ].join('&');

        fetch(`/Notifications/GetMoreArchivedNotifications?${options}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    let newNotifications = res.result.archivedNotifications
                    const newState = { ...staffNotifications };
                    newState.Archived = staffNotifications.Archived.concat(newNotifications);
                    setNotifications(newState);
                }
            });
    }

    //snooze notification
    function snoozeItem(index: number, groupId: string, snoozeType: string, item: any, snoozeDate: Date) {
        let sList = staffNotifications[groupId as keyof typeof GroupTypes];
        let dList = staffNotifications.Snoozed;

        var snoozeOptions = [
            `id=${item.id}`,
            `snoozeUntil=${snoozeType}`,
            `snoozeDate=${snoozeDate}`
        ].join('&');

        fetch(`/Notifications/SnoozeNotification?${snoozeOptions}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    fetchPayload();
                }
            });
    }

    //notification note
    function addItemNote(id: number, groupId: string, note: string) {
        let list = staffNotifications[groupId as keyof typeof GroupTypes];
        let updatedList = list.map(
            item => {
                if (item.id == id)
                    return { ...item, note: note };
                return (item);
            }
        );

        var noteOptions = [
            `notificationId=${id}`,
            `note=${note}`
        ].join('&');

        fetch(`/Notifications/SaveNote?${noteOptions}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    const newState = { ...staffNotifications };
                    newState[groupId as keyof typeof GroupTypes] = updatedList;
                    setNotifications(newState);
                }
            });
    }

    //add new notification
    function addNewItem(event: any) {
        event.preventDefault();

        var newNotificationOptions = [
            `title=${newNotificationFormData.newNotificationTitle}`,
            `body=${newNotificationFormData.newNotificationBody}`,
            `isHighPriority=${newNotificationFormData.newNotificationPriorityType}`,
            `snoozeUntil=${newNotificationFormData.newNotificationSnoozeType}`,
        ].join('&');

        fetch(`/Notifications/AddNewNotification?${newNotificationOptions}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage, res.secondaryErrorMessage);
                }
                else {
                    setAddNewModalStatus('');
                    displayAlertModal("is-success", "Success", "A new notification was successfully added!");
                    fetchPayload();
                }
            });
    }

    function updateNewItemForm(event: any) {
        setNewNotificationFormData({
            ...newNotificationFormData,
            [event.target.id]: event.target.value,
        });
    }

    function toggleAddNewModal() {
        if (addNewModalStatus == '') {
            setAddNewModalStatus('is-active');;
        }
        else {
            setAddNewModalStatus('');
        }
    }

    function displayAlertModal(type: string, title: string, message: string, secondaryMessage?: string) {
        const newState = { ...alertInfo };
        let alertMessage = message;
        if (secondaryMessage) {
            alertMessage = message + ": " + secondaryMessage
        }
        newState.alertTitle = title;
        newState.alertMessage = alertMessage;
        newState.alertType = type;
        setAlertInfo(newState);
        newState.alertModalStatus = 'is-active';
    }

    function closeAlertModal() {
        const newState = { ...alertInfo };
        newState.alertModalStatus = '';
        newState.alertMessage = '';
        newState.alertTitle = '';
        newState.alertType = '';
        setAlertInfo(newState);
    }

    function processTimesheetRequest(item: any, requestId: number, action: string) {
        let notificationId = item.id;
        if (action == "approve")
            return approveTimesheetRequest(requestId, notificationId, item);
        if (action == "deny")
            return denyTimesheetRequest(requestId, notificationId, item);
    }

    function approveTimesheetRequest(requestId: number, notificationId: number, item: any) {

        fetch(`/Notifications/ApproveTimesheetRequest?requestId=${requestId}&notificationId=${notificationId}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage);
                }
                else {
                    displayAlertModal("is-success", "Success", "This request was successfully approved!");
                    archiveItem(item);
                    fetchPayload();
                }
            })
    }

    function denyTimesheetRequest(requestId: number, notificationId: number, item: any) {

        fetch(`/Notifications/DenyTimesheetRequest?requestId=${requestId}&&notificationId=${notificationId}`)
            .then(res => res.json())
            .then(res => {
                if (res.isError) {
                    displayAlertModal("is-danger", "Error", res.errorMessage);
                }
                else {
                    displayAlertModal("is-danger", "Deny Confirmation", "This request was successfully denied!");
                    archiveItem(item);
                    fetchPayload();
                }
            })
    }

    function toggleArchiveGroup() {
        if (archiveGroupStatus == "") {
            setArchiveGroupStatus('is-hidden');
            setGroupWidth(GroupWidth.wide);
        }
        else {
            setArchiveGroupStatus("");
            setGroupWidth(GroupWidth.narrow);
        }
    }

    //render
    return (
        <div>
                <nav className="top-menu navbar has-shadow">
                    <div className="container">
                    <ExtranetLogo></ExtranetLogo>
                    <ApplicationName name="My Notifications"></ApplicationName>
                    <span className="buttons">
                        <SearchBar placeHolder="Search Notifications..." getSearchResults={getSearchResults}></SearchBar>
                        <button className="button is-primary" onClick={toggleAddNewModal}>
                            <span className="icon">
                                <FontAwesomeIcon icon={["fas", "plus"]} />
                            </span>
                            <span>Add New</span>
                        </button>
                        <NotificationSettingComponent displayAlertModal={displayAlertModal}></NotificationSettingComponent>
                        <button className="button is-info is-outlined toggle-archive-div">
                            <span className="icon">
                                <input type="checkbox" onClick={toggleArchiveGroup} />
                            </span>
                            <span>Show Archived</span>
                        </button>
                    </span>
                    </div>
                </nav>
                <div className="columns is-multiline is-centered notifications-container">
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className={`column is-${groupWidth}`}>
                            <article className="message is-success">
                            <div className="message-header">
                                <p>ACTIVE / NEW {staffNotifications.Active.length >0 && <span>({staffNotifications.Active.length})</span>}</p>
                                </div>
                                <div className="message-body">
                                <Droppable key={GroupTypes.Active} droppableId={`${GroupTypes.Active}`} type="group">
                                    {(provided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps} className="droppable-height">
                                                {
                                                    staffNotifications.Active.map(
                                                        (item, index) =>
                                                            <Draggable draggableId={`${item.id}`} key={item.id} index={index}>
                                                                {(provided) => (
                                                                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                        <NotificationCard item={item} groupId="Active" index={index} removeItem={confirmRemoveItem} archiveItem={archiveItem} processTimesheetRequest={processTimesheetRequest} snoozeItem={snoozeItem} addItemNote={addItemNote}></NotificationCard>
                                                                    </div>
                                                             )}
                                                            </Draggable>
                                                    )
                                                }
                                                {provided.placeholder}
                                        </div>
                                    )}
                                   </Droppable>
                                </div>
                            </article>
                        </div>
                    <div className={`column is-${groupWidth}`}>
                            <article className="message is-warning">
                            <div className="message-header">
                                <p>LATER / SNOOZED {staffNotifications.Snoozed.length >0 && <span>({staffNotifications.Snoozed.length})</span>}</p>
                                </div>
                                <div className="message-body">
                                    <Droppable key={GroupTypes.Snoozed} droppableId={`${GroupTypes.Snoozed}`} type="group">
                                        {(provided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps} className="droppable-height">
                                                {
                                                    staffNotifications.Snoozed.map(
                                                        (item, index) =>
                                                            <Draggable draggableId={`${item.id}`} key={item.id} index={index}>
                                                                {(provided) => (
                                                                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                        <NotificationCard item={item} groupId="Snoozed" index={index} removeItem={confirmRemoveItem} archiveItem={archiveItem} processTimesheetRequest={processTimesheetRequest} snoozeItem={snoozeItem} addItemNote={addItemNote}></NotificationCard>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                    )
                                                }
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </article>
                    </div>
                    <div className={`column is-3 ${archiveGroupStatus}`}>
                            <article className="message is-info">
                            <div className="message-header">
                                <p>ARCHIVED {staffNotifications.Archived.length >0 && <span>({staffNotifications.Archived.length})</span>}</p>
                                </div>
                            <div className="message-body">
                                <Droppable key={GroupTypes.Archived} droppableId={`${GroupTypes.Archived}`} type="group">
                                    {(provided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps} className="droppable-height">
                                            {
                                                staffNotifications.Archived.map(
                                                    (item, index) =>
                                                        <Draggable draggableId={`${item.id}`} key={item.id} index={index}>
                                                            {(provided) => (
                                                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                    <NotificationCard item={item} groupId="Archived" index={index} removeItem={confirmRemoveItem} snoozeItem={snoozeItem} processTimesheetRequest={processTimesheetRequest} addItemNote={addItemNote}></NotificationCard>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                )
                                            }
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                                {staffNotifications.Archived.length >=5
                                    && <div className="load-more-div has-text-centered">
                                    <button className="button is-success is-outlined is-italic" onClick={(event)=>getMoreArchivedItems(event)}>Load more...</button>
                                    </div>
                                }
                            </div>
                            </article>
                        </div>
                    </DragDropContext>
            </div>
            <div className={`modal ${addNewModalStatus} add-new-notification-modal`}>
                <div className="modal-background" />
                <form>
                    <div className="modal-card">
                        <header className="modal-card-head">
                        <p className="modal-card-title">New Notification</p>
                        <button
                            onClick={toggleAddNewModal}
                            className="delete"
                            aria-label="close"
                        />
                        </header>
                        <section className="modal-card-body">
                        <div className="field">
                            <div className="control">
                                <input
                                    id="newNotificationTitle"
                                    onChange={updateNewItemForm}
                                    className="input"
                                    type="text"
                                    placeholder="Subject..."
                                />
                            </div>
                        </div>
                        <div className="field">
                            <div className="control">
                                <textarea
                                    id="newNotificationBody"
                                    onChange={updateNewItemForm}
                                    className="textarea"
                                    placeholder="Description..."
                                />
                            </div>
                        </div>
                        <div className="field is-horizontal">
                            <div className="field-body">
                                <div className="field is-grouped">
                                    <label className="label">Priority</label>
                                    <div className="control">
                                        <div className="select is-small">
                                                <select id="newNotificationPriorityType" onClick={updateNewItemForm}>
                                                <option value="0">Normal</option>
                                                <option value="1">High</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="field is-grouped">
                                    <label className="label">Remind me</label>
                                    <div className="control">
                                            <div className="select is-small">
                                                <select id="newNotificationSnoozeType" onClick={updateNewItemForm}>
                                                    <option value="OneHour">in 1 hour</option>
                                                    <option value="FourHour">in 4 hours</option>
                                                    <option value="Tomorrow">Tomorrow</option>
                                                    <option value="NextWeek">Next Week</option>
                                                </select>
                                            </div>
                                        </div>
                                </div>
                            </div>
                        </div>
                    </section>
                        <footer className="modal-card-foot">
                            <button onClick={addNewItem} className="button is-success">Add Notification</button>
                            <button onClick={toggleAddNewModal} className="button">
                                Cancel
                            </button>
                        </footer>
                    </div>
                </form>
            </div>
            <div className={`modal ${alertInfo.alertModalStatus}`}>
                <AlertDialog alertType={alertInfo.alertType} alertTitle={alertInfo.alertTitle} alertMessage={alertInfo.alertMessage} closeAlertModal={closeAlertModal} deleteAlertModal={removeItem}></AlertDialog>
            </div>
        </div>
    );

        
};

export default NotificationsApp;